import React from 'react';
import { RefreshCw, Clock, Package, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const RefundPolicyPage: React.FC = () => {
  const refundConditions = [
    {
      icon: Package,
      title: 'Eligible Items',
      content: 'Unopened, unused products in original packaging with all seals intact.',
      status: 'eligible',
    },
    {
      icon: Clock,
      title: 'Timeframe',
      content: 'Returns must be initiated within 7 days of delivery date.',
      status: 'eligible',
    },
    {
      icon: AlertCircle,
      title: 'Condition',
      content: 'Products must be in the same condition as when delivered.',
      status: 'eligible',
    },
    {
      icon: XCircle,
      title: 'Non-Eligible',
      content: 'Perishable items, opened products, or items damaged by customer.',
      status: 'ineligible',
    },
    {
      icon: CheckCircle,
      title: 'Refund Process',
      content: 'Refunds processed within 5-7 business days after return inspection.',
      status: 'process',
    },
    {
      icon: RefreshCw,
      title: 'Return Shipping',
      content: 'Free return shipping for defective or incorrect items.',
      status: 'process',
    },
  ];

  const steps = [
    {
      step: '1',
      title: 'Contact Support',
      description: 'Email support@angelcite.com within 7 days of delivery with your order number and reason for return.',
    },
    {
      step: '2',
      title: 'Receive Instructions',
      description: 'We\'ll provide you with return instructions and a prepaid shipping label if applicable.',
    },
    {
      step: '3',
      title: 'Ship Your Return',
      description: 'Package the item securely in its original packaging and ship it back to us.',
    },
    {
      step: '4',
      title: 'Receive Refund',
      description: 'Once we receive and inspect the item, we\'ll process your refund within 5-7 business days.',
    },
  ];

  return (
    <div className="bg-dark-dark dark:bg-white transition-colors duration-300">
      {/* Hero Section */}
      <section className="relative py-12 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent via-accent-light to-primary opacity-90" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <RefreshCw className="w-16 h-16 text-white mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Refund Policy
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Last updated: January 15, 2024
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        {/* Introduction */}
        <div className="text-center mb-12 md:mb-16">
          <p className="text-lg text-gray-300 dark:text-gray-600 max-w-3xl mx-auto transition-colors duration-300">
            We want you to be completely satisfied with your purchase. If you're not happy with your order, 
            here's everything you need to know about our refund process.
          </p>
        </div>

        {/* Conditions Grid */}
        <div className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-white dark:text-dark-dark text-center mb-8 transition-colors duration-300">
            Refund Conditions
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {refundConditions.map((condition, index) => (
              <div
                key={index}
                className={`p-6 rounded-lg border-2 transition-all duration-300 ${
                  condition.status === 'eligible'
                    ? 'border-accent/30 dark:border-primary/30 bg-accent/5 dark:bg-primary/5'
                    : condition.status === 'ineligible'
                    ? 'border-red-300 dark:border-red-300 bg-red-50 dark:bg-red-900/10'
                    : 'border-blue-300 dark:border-blue-300 bg-blue-50 dark:bg-blue-900/10'
                }`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    condition.status === 'eligible'
                      ? 'bg-accent/20 dark:bg-primary/20'
                      : condition.status === 'ineligible'
                      ? 'bg-red-100 dark:bg-red-900/20'
                      : 'bg-blue-100 dark:bg-blue-900/20'
                  }`}>
                    <condition.icon className={`w-5 h-5 ${
                      condition.status === 'eligible'
                        ? 'text-accent dark:text-primary'
                        : condition.status === 'ineligible'
                        ? 'text-red-500'
                        : 'text-blue-500'
                    }`} />
                  </div>
                  <h3 className="font-semibold text-white dark:text-dark-dark transition-colors duration-300">
                    {condition.title}
                  </h3>
                </div>
                <p className="text-gray-300 dark:text-gray-600 transition-colors duration-300">
                  {condition.content}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Return Process */}
        <div className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-white dark:text-dark-dark text-center mb-8 transition-colors duration-300">
            Return Process
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step) => (
              <div key={step.step} className="relative">
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-12 h-12 bg-accent dark:bg-primary text-white rounded-full flex items-center justify-center font-bold text-lg z-10 transition-colors duration-300">
                  {step.step}
                </div>
                <div className="pt-10 pb-6 px-6 bg-dark-light dark:bg-gray-50 rounded-lg h-full transition-colors duration-300">
                  <h3 className="text-lg font-semibold text-white dark:text-dark-dark mb-3 text-center transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-gray-300 dark:text-gray-600 text-sm text-center transition-colors duration-300">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Important Information */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Refund Methods */}
          <div className="p-6 bg-dark-light dark:bg-gray-50 rounded-lg transition-colors duration-300">
            <h3 className="text-xl font-semibold text-white dark:text-dark-dark mb-4 transition-colors duration-300">
              Refund Methods
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-accent dark:text-primary mt-0.5 flex-shrink-0" />
                <span className="text-gray-300 dark:text-gray-600 transition-colors duration-300">
                  Refunds are issued to the original payment method
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-accent dark:text-primary mt-0.5 flex-shrink-0" />
                <span className="text-gray-300 dark:text-gray-600 transition-colors duration-300">
                  Credit card refunds take 5-10 business days to appear on your statement
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-accent dark:text-primary mt-0.5 flex-shrink-0" />
                <span className="text-gray-300 dark:text-gray-600 transition-colors duration-300">
                  PayPal refunds are usually instant
                </span>
              </li>
            </ul>
          </div>

          {/* Exceptions */}
          <div className="p-6 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-300 rounded-lg transition-colors duration-300">
            <h3 className="text-xl font-semibold text-white dark:text-dark-dark mb-4 transition-colors duration-300">
              Important Exceptions
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-300 dark:text-gray-600 transition-colors duration-300">
                  Perishable items cannot be returned unless defective
                </span>
              </li>
              <li className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-300 dark:text-gray-600 transition-colors duration-300">
                  Items damaged due to improper storage or handling
                </span>
              </li>
              <li className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-300 dark:text-gray-600 transition-colors duration-300">
                  Opened or partially used products
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-12 text-center">
          <h3 className="text-xl font-semibold text-white dark:text-dark-dark mb-4 transition-colors duration-300">
            Need Help?
          </h3>
          <p className="text-gray-300 dark:text-gray-600 mb-6 max-w-2xl mx-auto transition-colors duration-300">
            If you have questions about our refund policy or need assistance with a return, our support team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:support@angelcite.com"
              className="inline-flex items-center justify-center font-medium rounded-lg px-6 py-3 bg-accent dark:bg-primary text-white hover:bg-accent-dark dark:hover:bg-primary-dark transition-colors duration-300"
            >
              Email Support
            </a>
            <a
              href="/contact"
              className="inline-flex items-center justify-center font-medium rounded-lg px-6 py-3 border-2 border-accent dark:border-primary text-accent dark:text-primary hover:bg-accent dark:hover:bg-primary hover:text-white transition-colors duration-300"
            >
              Contact Form
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicyPage;